import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Location, Organization, World } from "@dreamhub/designsystem/icons";
import { TRANSLATION_PREFIX } from "@/components/global/Chat/components/meetingPrep/consts";
import MeetingPrepEmptyState from "@/components/global/Chat/components/meetingPrep/components/MeetingPrepEmptyState";
import { MeetingPrepBrief } from "@/entities/meetingPrep/types";

const stripUrl = (url: string): string =>
  url
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");

const toHttpUrl = (url: string): string | null => {
  try {
    const parsed = new URL(
      /^[a-z][a-z\d+\-.]*:/i.test(url) ? url : `https://${url}`
    );
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};

const OverviewItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <div className="flex flex-1 flex-col gap-1 min-w-0">
    <div className="flex items-center gap-1">
      <IconContainer size="sm" color="tertiary">
        {icon}
      </IconContainer>
      <Text as="span" variant="bodyTertiary">
        {label}
      </Text>
    </div>
    <div className="break-words">{children}</div>
  </div>
);

const StackedField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <div className="flex flex-col gap-1 px-4">
    <Text variant="bodyTertiary">{label}</Text>
    <Text variant="body" className="whitespace-pre-wrap break-words">
      {children}
    </Text>
  </div>
);

// Model-authored fields can be blank; trim once so checks and guards agree.
const trimOrEmpty = (value?: string): string => value?.trim() ?? "";

const OverviewTab: React.FC<{ brief: MeetingPrepBrief }> = ({ brief }) => {
  const { t } = useTranslation();
  const { overview } = brief;

  const location = trimOrEmpty(overview.location);
  const industry = trimOrEmpty(overview.industry);
  // Derived, not raw: an unusable website renders nothing, so it isn't content.
  const websiteHref = overview.website
    ? toHttpUrl(trimOrEmpty(overview.website))
    : null;
  const whatTheyDo = trimOrEmpty(brief.whatTheyDo);
  const problemTheySolve = trimOrEmpty(brief.problemTheySolve);
  const targetCompanies = trimOrEmpty(brief.whoTheySellTo?.targetCompanies);
  const targetUsers = trimOrEmpty(brief.whoTheySellTo?.targetUsers);

  // Each row is guarded on its own fields: an empty one still paints its
  // padding, which reads as a blank band above whatever renders below it.
  const hasMetadata = Boolean(location || industry || websiteHref);
  const hasDetails = Boolean(
    whatTheyDo || problemTheySolve || targetCompanies || targetUsers
  );

  // The company name lives in the card header, not this tab.
  if (!hasMetadata && !hasDetails) {
    return <MeetingPrepEmptyState tab="overview" />;
  }

  return (
    <div className="flex flex-col">
      {hasMetadata && (
        <div className="flex gap-4 p-4">
          {location && (
            <OverviewItem
              icon={<Location />}
              label={t(`${TRANSLATION_PREFIX}.labels.hq`)}
            >
              <Text variant="body">{location}</Text>
            </OverviewItem>
          )}
          {industry && (
            <OverviewItem
              icon={<Organization />}
              label={t(`${TRANSLATION_PREFIX}.labels.industry`)}
            >
              <Text variant="body">{industry}</Text>
            </OverviewItem>
          )}
          {websiteHref ? (
            <OverviewItem
              icon={<World />}
              label={t(`${TRANSLATION_PREFIX}.labels.website`)}
            >
              <a
                href={websiteHref}
                target="_blank"
                rel="noreferrer"
                className="break-all"
              >
                <Text as="span" variant="body" className="text-link">
                  {stripUrl(websiteHref)}
                </Text>
              </a>
            </OverviewItem>
          ) : null}
        </div>
      )}
      {hasDetails && (
        <div className="flex flex-col gap-4 pb-4">
          {whatTheyDo && (
            <StackedField label={t(`${TRANSLATION_PREFIX}.labels.whatTheyDo`)}>
              {whatTheyDo}
            </StackedField>
          )}
          {problemTheySolve && (
            <StackedField
              label={t(`${TRANSLATION_PREFIX}.labels.problemTheySolve`)}
            >
              {problemTheySolve}
            </StackedField>
          )}
          {targetCompanies && (
            <StackedField
              label={t(`${TRANSLATION_PREFIX}.labels.targetCompanies`)}
            >
              {targetCompanies}
            </StackedField>
          )}
          {targetUsers && (
            <StackedField label={t(`${TRANSLATION_PREFIX}.labels.targetUsers`)}>
              {targetUsers}
            </StackedField>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
